"use client";

import { useState, useEffect, useCallback } from "react";
import FilterGroup from "@/components/ui/FilterGroup";
import { useRouter } from "next/navigation";
import { apiRequest } from "@/lib/api/config";

/**
 * HeaderProductSearch uses FilterGroup to provide a searchable product dropdown in the header.
 * It fetches data dynamically only when at least 3 characters are typed.
 */
export default function HeaderProductSearch({ 
  className = "w-full",
  searchClassName = "!max-w-none !px-0",
  showBorder = false,
  placeholder = "محصول خود را اینجا جستجو کنید"
}) {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const fetchResults = useCallback(async (searchQuery) => {
    if (searchQuery.length < 3) {
      setProducts([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const response = await apiRequest("products", {
        method: "GET",
        params: { search: searchQuery },
      });
      // Handle the nested structure from apiRequest
      const data = response?.data || response;
      const productsData = Array.isArray(data) ? data : (data.data || []);
      const totalCount = response?.total || productsData.length;
      
      setProducts(productsData);
      setTotal(totalCount);
    } catch (error) {
      console.error("Search error:", error);
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 3) {
        fetchResults(query);
      } else {
        setProducts([]);
        setTotal(0);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, fetchResults]);

  const handleSelect = (product) => {
    if (product) {
      router.push(`/product/${product.slug}-${product.id}`);
      setQuery(""); // Clear search to keep placeholder visible
    }
  };

  const handleEnter = (searchQuery) => {
    if (searchQuery.trim()) {
      router.push(`/product?search=${encodeURIComponent(searchQuery)}`);
      setQuery("");
    }
  };

  const productItems = products.map((product) => ({
    ...product,
    label: product.title,
    value: product.slug,
  }));

  return (
    <div className={className}>
      <FilterGroup
        items={productItems}
        onSelect={handleSelect}
        onSearchChange={setQuery}
        searchValue={query}
        showHorizontal={false}
        searchPlaceholder={placeholder}
        className="w-full"
        showSearch={true}
        searchClassName={searchClassName}
        loading={loading}
        minSearchLength={3}
        total={total}
        showBorder={showBorder}
        viewAllLink={`/product?search=${encodeURIComponent(query)}`}
        viewAllText="مشاهده همه نتایج"
        onEnter={handleEnter}
      />
    </div>
  );
}

